<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { getDateAndHour, getDifference } from "@/common/utils/dates";

import { AnalysisListType } from "@/common/types/analysis";
import { svgIcon } from "@/common/components";

const { t, tm } = useI18n();

const emit = defineEmits(["edit", "copy", "delete"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisListType;
    editable?: boolean;
  }>(),
  {
    editable: true,
  }
);

const statusList: { [key: string]: string } = {
  PENDING: "time",
  STARTED: "time-b",
  WARNING: "info",
  CANCELED: "close",
  CONCLUDED: "confirmed",
  FINISHED: "confirmed",
};

const statusColor: { [key: string]: string } = {
  PENDING: "#666",
  STARTED: "#666",
  WARNING: "orange",
  CANCELED: "red",
  CONCLUDED: "green",
  FINISHED: "green",
};

const enumAnalysisStatus = computed(
  () => tm("enums.analysis_status") as Record<string, string>
);

const methods = computed(
  () => props.data?.methods?.map((item) => item.code) ?? []
);

const samples = computed(
  () => props.data?.samples?.map((item) => item.code) ?? []
);

const equipments = computed(
  () => props.data?.equipments?.map((item) => item.code) ?? []
);

const results = computed(
  () => props.data?.results?.map((item) => item.name) ?? []
);

const forms = computed(() => props.data?.forms?.map((item) => item.code) ?? []);
</script>

<template>
  <section class="card-analysis">
    <div class="content">
      <div class="code">
        <span>
          {{ data.code }}
        </span>
        <span :style="{ color: statusColor[data.status] }">
          <svg-icon size="1.5rem" :icon="statusList[data.status]" />
        </span>
      </div>
      <div class="status">
        <span class="label"> {{ t("common.labels.status") }}: </span>
        <span class="value uppercase">
          {{ enumAnalysisStatus[data?.status] }}
        </span>
      </div>

      <template v-if="data.start_date">
        <div class="status">
          <span class="label"> {{ t("common.labels.start") }}: </span>
          <span class="value">
            {{ getDateAndHour(data.start_date) }}
          </span>
        </div>
      </template>

      <template v-if="data.end_date">
        <div class="status">
          <span class="label"> {{ t("common.labels.termination") }}: </span>
          <span class="value">
            {{ getDateAndHour(data.end_date) }}
          </span>
        </div>
      </template>

      <template v-if="data.start_date">
        <div class="time">
          <span class="label"> {{ t("common.labels.time") }}: </span>
          <span class="value">
            <template v-if="data.start_date && !data.end_date">
              {{
                getDifference(data.start_date, new Date().toISOString(), false)
                  .text
              }}
            </template>
            <template v-else-if="data.start_date && data.end_date">
              {{ getDifference(data.start_date, data.end_date, false).text }}
            </template>
            <template v-else> 0h 0m </template>
          </span>
        </div>
      </template>

      <template v-if="samples.length > 0">
        <div class="employee">
          <span class="label">
            <template v-if="samples.length === 1">
              {{ t("common.labels.sample") }}:
            </template>
            <template v-else> {{ t("common.labels.samples") }}: </template>
          </span>
          <span class="value">
            {{ samples.join(", ") }}
          </span>
        </div>
      </template>

      <template v-if="forms.length > 0">
        <div class="employee">
          <span class="label">
            <template v-if="forms.length === 1">
              {{ t("common.labels.form") }}:
            </template>
            <template v-else> {{ t("common.labels.forms") }}: </template>
          </span>
          <span class="value">
            {{ forms.join(", ") }}
          </span>
        </div>
      </template>

      <template v-if="methods.length > 0">
        <div class="employee">
          <span class="label">
            <template v-if="methods.length === 1">
              {{ t("common.labels.method") }}:
            </template>
            <template v-else> {{ t("common.labels.methods") }}: </template>
          </span>
          <span class="value">
            {{ methods.join(", ") }}
          </span>
        </div>
      </template>

      <template v-if="equipments.length > 0">
        <div class="employee">
          <span class="label">
            <template v-if="equipments.length === 1">
              {{ t("common.labels.equipment") }}:
            </template>
            <template v-else> {{ t("common.labels.equipments") }}: </template>
          </span>
          <span class="value">
            {{ equipments.join(", ") }}
          </span>
        </div>
      </template>

      <template v-if="data.member_id">
        <div class="technical">
          <span class="label"> {{ t("common.labels.technical") }}: </span>
          <span class="value">
            {{ data.technical?.first_name }} {{ data.technical?.last_name }}
          </span>
        </div>
      </template>

      <template v-if="data.employee_id">
        <div class="employee">
          <span class="label"> {{ t("common.labels.employee") }}: </span>
          <span class="value">
            {{ data.employee?.first_name }} {{ data.employee?.last_name }}
          </span>
        </div>
      </template>

      <template v-if="results.length > 0">
        <div class="employee">
          <span class="label">
            <template v-if="results.length === 1">
              {{ t("common.labels.result") }}:
            </template>
            <template v-else> {{ t("common.labels.results") }}: </template>
          </span>
          <span class="value">
            {{ results.join(", ") }}
          </span>
        </div>
      </template>
    </div>

    <div class="actions">
      <button class="btn btn-sm btn-white" @click="emit('edit')">
        <svg-icon icon="edit" size="1.25rem" />
        <span class="text-3">
          {{ t("common.labels.edit") }}
        </span>
      </button>
      <!-- <button class="btn btn-sm btn-white" @click="emit('copy')">
        <svg-icon icon="copy" size="1.25rem" />
        <span class="text-3">
          {{ t("common.labels.copy") }}
        </span>
      </button> -->
      <button class="btn btn-sm btn-delete" @click="emit('delete')">
        <svg-icon icon="trash" size="1.25rem" />
        <span class="text-3">
          {{ t("common.labels.delete") }}
        </span>
      </button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.card-analysis {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 2rem;
  background: var(--white-off);
  padding-block: 0.75rem;
  padding-inline: 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.75rem;

  &:hover {
    border-color: #000;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .code {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      min-width: 8rem;
      max-width: 20rem;
      gap: 1rem;
      background: var(--white);
      border-radius: 0.5rem;
      padding-inline: 0.5rem;
      padding-block: 0.25rem;
      font-size: 1.15rem;
      font-weight: bold;
      margin-block-end: 0.5rem;
    }

    .status,
    .time,
    .employee,
    .technical {
      // display: flex;
      // flex-direction: row;
      display: grid;
      grid-template-columns: 7.25rem 1fr;
      gap: 0.5rem;
      padding-inline: 0.5rem;

      .label {
        text-transform: uppercase;
        color: var(--text-medium);
        font-size: 0.75rem;
      }

      .value {
        font-weight: bold;
        font-size: 0.85rem;
        color: var(--text);
      }
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 0.75rem;

    button {
      text-transform: uppercase;
      padding-inline: 0.25rem;
      padding-block: 0.25rem;
      min-width: 6.25rem;
    }
  }
}

@media screen and (max-width: 1024px) {
  .card-analysis {
    flex-direction: column;

    .content {
      .code {
        font-size: 1rem;
      }

      .status,
      .time,
      .employee,
      .technical {
        font-size: 0.875rem;
      }
    }

    .actions {
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;

      button {
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        padding-block: 0.5rem;

        .svgicon {
          width: 1.2rem;
          height: 1.2rem;
        }
      }
    }
  }
}
</style>
